import {
    Button as HeadlessButton,
    type ButtonProps as HeadlessButtonProps,
} from "lib/headlessui/components/button/button";
import { CloseButton } from "lib/headlessui/components/close-button/close-button";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { LayoutGroup, motion } from "framer-motion";
import React, { forwardRef, useId } from "react";
import { Link } from "./link";

export type SidebarMode = "full" | "icons";

export function Sidebar({
    className,
    mode,
    ...props
}: React.ComponentPropsWithoutRef<"nav"> & { mode: SidebarMode }) {
    return (
        <nav
            {...props}
            className={clsx(
                className,
                "flex h-full min-h-0 flex-col",
                mode === "icons" &&
                    "w-16 bg-white dark:bg-zinc-900 border-r border-zinc-200 dark:border-zinc-700",
            )}
        />
    );
}

export function SidebarHeader({
    className,
    ...props
}: React.ComponentPropsWithoutRef<"div">) {
    return (
        <div
            {...props}
            className={clsx(
                className,
                "flex flex-col border-b border-zinc-950/5 p-4 dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:mt-2.5",
            )}
        />
    );
}

export const SidebarBody = React.forwardRef<
    HTMLDivElement,
    React.ComponentPropsWithoutRef<"div"> & { mode: SidebarMode }
>(function SidebarBody({ className, mode, ...props }, ref) {
    return (
        <div
            ref={ref}
            {...props}
            className={clsx(
                className,
                "flex flex-1 flex-col overflow-y-auto overflow-x-hidden",
                mode === "icons" ? "py-3" : "pt-2 pb-4",
                "[&>[data-slot=section]+[data-slot=section]]:mt-8",
            )}
        />
    );
});

export function SidebarFooter({
    className,
    mode,
    ...props
}: React.ComponentPropsWithoutRef<"div"> & { mode: SidebarMode }) {
    return (
        <div
            {...props}
            className={clsx(
                className,
                "flex flex-col",
                mode === "icons"
                    ? "items-center border-t border-zinc-200 dark:border-zinc-800 py-3"
                    : "border-t border-zinc-950/5 p-4 dark:border-white/5",
                "[&>[data-slot=section]+[data-slot=section]]:mt-2.5",
            )}
        />
    );
}

export function SidebarSection({
    className,
    mode,
    ...props
}: React.ComponentPropsWithoutRef<"div"> & { mode: SidebarMode }) {
    const id = useId();

    return (
        <LayoutGroup id={id}>
            <div
                {...props}
                data-slot="section"
                className={clsx(
                    className,
                    "flex flex-col",
                    mode === "icons" ? "items-center gap-1.5" : "gap-0.5",
                )}
            />
        </LayoutGroup>
    );
}

export function SidebarDivider({
    className,
    ...props
}: React.ComponentPropsWithoutRef<"hr">) {
    return (
        <hr
            {...props}
            className={clsx(
                className,
                "my-4 border-t border-zinc-950/5 lg:-mx-4 dark:border-white/5",
            )}
        />
    );
}

export function SidebarSpacer({
    className,
    ...props
}: React.ComponentPropsWithoutRef<"div">) {
    return (
        <div aria-hidden="true" {...props} className={clsx(className, "mt-8 flex-1")} />
    );
}

export function SidebarHeading({
    className,
    ...props
}: React.ComponentPropsWithoutRef<"h3">) {
    return (
        <h3
            {...props}
            className={clsx(
                className,
                "mb-1 px-2 text-xs/6 font-medium text-zinc-500 dark:text-zinc-400",
            )}
        />
    );
}

export const SidebarItem = forwardRef(function SidebarItem(
    {
        current,
        className,
        children,
        expandable,
        expanded,
        onExpandToggle,
        onClick,
        mode,
        ...props
    }: {
        current?: boolean;
        className?: string;
        children: React.ReactNode;
        expandable?: boolean;
        expanded?: boolean;
        onExpandToggle?: () => void;
        onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
        mode?: SidebarMode;
    } & (
        | Omit<HeadlessButtonProps, "as" | "className">
        | Omit<HeadlessButtonProps<typeof Link>, "as" | "className">
    ),
    ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>,
) {
    const roundedClass = mode === "full" ? "lg:rounded-lg" : "";
    const classes = clsx(
        "flex w-full items-center gap-3 px-2 py-2.5 text-left text-base/6 font-medium text-zinc-950 sm:py-2 sm:text-sm/5",
        "*:data-[slot=icon]:size-6 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:stroke-brand-800 sm:*:data-[slot=icon]:size-5",
        "*:last:data-[slot=icon]:ml-auto *:last:data-[slot=icon]:size-5 sm:*:last:data-[slot=icon]:size-4",
        "*:data-[slot=avatar]:-m-0.5 *:data-[slot=avatar]:size-7 sm:*:data-[slot=avatar]:size-6",
        roundedClass,
        "data-hover:bg-zinc-950/5",
        "data-active:bg-zinc-950/5",
        "data-current:bg-brand-700 data-current:text-white data-current:*:data-[slot=icon]:stroke-white",
        "data-current:data-hover:bg-zinc-950/5 data-current:data-hover:text-zinc-950 data-current:data-hover:*:data-[slot=icon]:stroke-brand-800",
        "dark:text-white dark:*:data-[slot=icon]:stroke-brand-400",
        "dark:data-hover:bg-brand-950/30 dark:data-hover:*:data-[slot=icon]:stroke-brand-400",
        "dark:data-active:bg-brand-950/30 dark:data-active:*:data-[slot=icon]:stroke-brand-400",
        "dark:data-current:bg-brand-900/40 dark:data-current:text-brand-100 dark:data-current:*:data-[slot=icon]:stroke-brand-200",
    );

    const handleMainClick = (
        e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    ) => {
        if (onClick) {
            onClick(e);
        } else {
            if (expandable && onExpandToggle && !expanded) {
                onExpandToggle();
            }
        }
    };

    const handleChevronClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (onExpandToggle) {
            onExpandToggle();
        }
    };

    const chevronIcon = expandable && (
        <button
            onClick={handleChevronClick}
            className="ml-auto p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded"
            aria-label={expanded ? "Collapse section" : "Expand section"}>
            <ChevronRightIcon
                className={clsx(
                    "w-4 h-4 transition-transform duration-100 ease-in-out",
                    expanded && "rotate-90",
                )}
            />
        </button>
    );

    return (
        <span className={clsx(className, "relative")}>
            {current && (
                <motion.span
                    layoutId="current-indicator"
                    className="absolute inset-y-2 -left-4 w-0.5 rounded-full bg-brand-700 dark:bg-brand-300"
                />
            )}
            {"href" in props ? (
                <CloseButton
                    as={Link}
                    {...props}
                    className={classes}
                    data-current={current ? "true" : undefined}
                    onClick={handleMainClick}
                    target={props.href?.startsWith("http") ? "_blank" : undefined}
                    rel={
                        props.href?.startsWith("http") ? "noopener noreferrer" : undefined
                    }
                    ref={ref}>
                    {children}
                    {chevronIcon}
                </CloseButton>
            ) : (
                <HeadlessButton
                    {...props}
                    className={clsx("cursor-default", classes)}
                    data-current={current ? "true" : undefined}
                    onClick={handleMainClick}
                    ref={ref}>
                    {children}
                    {chevronIcon}
                </HeadlessButton>
            )}
        </span>
    );
});

export function SidebarLabel({
    className,
    ...props
}: React.ComponentPropsWithoutRef<"span">) {
    return <span {...props} className={clsx(className, "truncate")} />;
}
